/**
 * Widget i18n — bundled translations for every visible string the widget
 * renders without operator input. Tiny on purpose: the widget is a
 * "drop-on-the-homepage pill bar", so its surface is small.
 *
 * Adding a language:
 *   1. Append the ISO code to `WidgetLocale`
 *   2. Add the matching key block to `WIDGET_I18N`
 *   3. List it under `KNOWN_LOCALES` in `pickWidgetLocale`
 *
 * Operator-supplied overrides (`options.buttonText`, `options.placeholder`)
 * always win over the i18n defaults — a host who wants their own copy
 * never has to fight us for it. The host passes `locale: 'en'` to flip
 * the *fallback* labels and the in-widget error messages; their custom
 * `buttonText` would still render verbatim if set.
 */

export type WidgetLocale = 'de' | 'en'

export interface WidgetStrings {
  buttonText: string
  buttonSubmitting: string
  placeholder: string
  errPickAddress: string
  noResults: string
  errRedirect: string
  ariaSearchLabel: string
}

export const WIDGET_I18N: Record<WidgetLocale, WidgetStrings> = {
  de: {
    buttonText: 'Tarife & Preise anzeigen',
    buttonSubmitting: '…',
    placeholder: 'Deine vollständige Adresse',
    errPickAddress: 'Bitte eine Adresse aus der Vorschlagsliste auswählen.',
    noResults: 'Keine Treffer — bitte präziser tippen (PLZ + Stadt + Straße).',
    errRedirect: 'Weiterleitung fehlgeschlagen',
    ariaSearchLabel: 'Adresse eingeben',
  },
  en: {
    buttonText: 'Show plans & prices',
    buttonSubmitting: '…',
    placeholder: 'Your full address',
    errPickAddress: 'Please pick an address from the suggestions.',
    noResults: 'No matches — please be more specific (ZIP + city + street).',
    errRedirect: 'Redirect failed',
    ariaSearchLabel: 'Enter address',
  },
}

const KNOWN_LOCALES: WidgetLocale[] = ['de', 'en']

/**
 * Normalise whatever the host passes into a supported locale code.
 *
 * Accepts:
 *   - `"de"`, `"en"` (canonical)
 *   - `"de-DE"`, `"en-US"`, `"en_GB"` (BCP 47 / IETF tags — first 2 chars used)
 *   - `"DE"`, `"EN"` (case-insensitive)
 *   - `undefined` / unknown → falls back to `de`
 */
export function pickWidgetLocale(input?: string | null): WidgetLocale {
  if (!input) return 'de'
  const head = input.toString().toLowerCase().slice(0, 2)
  return (KNOWN_LOCALES as string[]).includes(head) ? (head as WidgetLocale) : 'de'
}
